import type { Process, ReferencedAppSettings } from "../types";
import type {
  EmailTemplate,
  ProcessStatus,
  ProcessPDFTemplate,
  CommentFlagOption,
  UserGroup,
  ObjectTypeWorkflowKindRelation,
  BusinessUnit,
  Region,
  ExtraJSON,
  IntegrationConfig,
  IntegrationToken,
  AppSettings,
  ProcessKind
} from "@certa/queries/types/appSettings.types";
import { emptyReferencedAppSettings } from "@certa/studio/src/hooks/dbOpsSlices/appsettings.slice";

/**
 * Helper function to merge arrays based on unique identifier
 * @param baseArray - Base array to merge with
 * @param referencedArray - Referenced array to merge
 * @param uniqueIdKey - Key to use as unique identifier
 * @returns Merged array with duplicates removed
 */
const mergeArraysByUniqueId = <T extends Record<string, any>>(
  baseArray: T[],
  referencedArray: T[],
  uniqueIdKey: keyof T
): T[] => {
  const referencedMap = new Map(
    referencedArray.map(item => [item[uniqueIdKey], item])
  );

  return baseArray
    .map(item =>
      referencedMap.has(item[uniqueIdKey])
        ? referencedMap.get(item[uniqueIdKey])!
        : item
    )
    .concat(
      referencedArray.filter(
        item =>
          !baseArray.some(
            baseItem => baseItem[uniqueIdKey] === item[uniqueIdKey]
          )
      )
    );
};

type AppSettingsArrays = Omit<AppSettings, "feExtraMetaData">;

// Optimized lookup maps for efficient O(1) access
type AppSettingsLookupMaps = {
  emailTemplateBySlug: Map<string, EmailTemplate>;
  processKindByTag: Map<string, ProcessKind>;
  processStatusByTag: Map<string, ProcessStatus>;
  groupByName: Map<string, UserGroup>;
  businessUnitByCode: Map<string, BusinessUnit>;
  regionByCode: Map<string, Region>;
  extraJSONByTag: Map<string, ExtraJSON>;
  processPDFTemplateByTag: Map<string, ProcessPDFTemplate>;
  commentFlagOptionByTag: Map<string, CommentFlagOption>;
  integrationTokenByTokenType: Map<string, IntegrationToken>;
  integrationConfigByApiType: Map<string, IntegrationConfig>;
  objectTypeWorkflowKindRelationByWorkflowKind: Map<
    string,
    ObjectTypeWorkflowKindRelation[]
  >;
};

// Collected references from processes
type CollectedReferences = {
  emailTemplateSlug: Set<string>;
  processKindTags: Set<string>;
  processStatusTags: Set<string>;
  groupNames: Set<string>;
  businessUnitCodes: Set<string>;
  regionCodes: Set<string>;
  extraJSONTags: Set<string>;
  processPDFTemplateTags: Set<string>;
  commentFlagOptionTags: Set<string>;
  workflowKindTags: Set<string>;
  integrationApiTypes: Set<string>;
  integrationTokenTokenTypes: Set<string>;
};

/**
 * Creates lookup maps from app settings for efficient O(1) access
 */
const createAppSettingsLookupMaps = (
  appSettings: AppSettings
): AppSettingsLookupMaps => {
  const objectTypeWorkflowKindRelationByWorkflowKind = new Map<
    string,
    ObjectTypeWorkflowKindRelation[]
  >();
  appSettings.objectTypeWorkflowKindRelation.forEach(relation => {
    const existing =
      objectTypeWorkflowKindRelationByWorkflowKind.get(relation.workflowKind) ||
      [];
    existing.push(relation);
    objectTypeWorkflowKindRelationByWorkflowKind.set(
      relation.workflowKind,
      existing
    );
  });

  return {
    emailTemplateBySlug: new Map(
      appSettings.emailTemplate.map(item => [item.slug, item])
    ),
    processKindByTag: new Map(
      appSettings.processKind.map(item => [item.tag, item])
    ),
    processStatusByTag: new Map(
      appSettings.processStatus.map(item => [item.tag, item])
    ),
    groupByName: new Map(appSettings.group.map(item => [item.name, item])),
    businessUnitByCode: new Map(
      appSettings.businessUnit.map(item => [item.code, item])
    ),
    regionByCode: new Map(appSettings.region.map(item => [item.code, item])),
    extraJSONByTag: new Map(
      appSettings.extraJSON.map(item => [item.tag, item])
    ),
    processPDFTemplateByTag: new Map(
      appSettings.processPDFTemplate.map(item => [item.tag, item])
    ),
    commentFlagOptionByTag: new Map(
      appSettings.commentFlagOption.map(item => [item.tag, item])
    ),
    integrationTokenByTokenType: new Map(
      appSettings.integrationToken.map(item => [item.tokenType, item])
    ),
    integrationConfigByApiType: new Map(
      appSettings.integrationConfig.map(item => [item.apiType, item])
    ),
    objectTypeWorkflowKindRelationByWorkflowKind
  };
};

/**
 * Collects all referenced values from processes first for efficient lookup
 */
const collectReferencesFromProcesses = (
  processes: Process[]
): CollectedReferences => {
  const references: CollectedReferences = {
    emailTemplateSlug: new Set(),
    processKindTags: new Set(),
    processStatusTags: new Set(),
    groupNames: new Set(),
    businessUnitCodes: new Set(),
    regionCodes: new Set(),
    extraJSONTags: new Set(),
    processPDFTemplateTags: new Set(),
    commentFlagOptionTags: new Set(),
    workflowKindTags: new Set(),
    integrationApiTypes: new Set(),
    integrationTokenTokenTypes: new Set()
  };

  // Default email template (even if not referred in config)
  references.emailTemplateSlug.add("mentions");

  processes.forEach(process => {
    // Process level references
    const proc = process.process;
    references.processKindTags.add(proc.kind.tag);
    if (proc.mentionsTemplate)
      references.emailTemplateSlug.add(proc.mentionsTemplate);
    if (proc.kind.defaultStatus)
      references.processStatusTags.add(proc.kind.defaultStatus);
    if (proc.kind.availableStatuses)
      proc.kind.availableStatuses.forEach(tag =>
        references.processStatusTags.add(tag)
      );
    if (proc.staticGroups)
      proc.staticGroups.forEach(name => references.groupNames.add(name));
    if (proc.dynamicGroupNames)
      proc.dynamicGroupNames.forEach(name => references.groupNames.add(name));
    if (proc.kind.visibleToStaticGroups)
      proc.kind.visibleToStaticGroups.forEach(name =>
        references.groupNames.add(name)
      );
    if (proc.kind.tag) references.workflowKindTags.add(proc.kind.tag);

    // Step level references
    Object.values(process.steps || {}).forEach(step => {
      if (step.staticGroups)
        step.staticGroups.forEach(name => references.groupNames.add(name));
      if (step.dynamicGroupNames)
        step.dynamicGroupNames.forEach(name => references.groupNames.add(name));
      if (step.myTasksGroups)
        step.myTasksGroups.forEach(name => references.groupNames.add(name));
      if (step.taggableUserGroups)
        step.taggableUserGroups.forEach(name =>
          references.groupNames.add(name)
        );
      if (step.taggableDynamicGroupNames)
        step.taggableDynamicGroupNames.forEach(name =>
          references.groupNames.add(name)
        );
      if (step.pdfConfig?.template)
        references.processPDFTemplateTags.add(step.pdfConfig.template);
    });

    // Field level references
    Object.values(process.fields || {}).forEach(field => {
      // Integration references
      const integrationApiType =
        field.fieldType === "integration" && field.extra?.api_type
          ? field.extra.api_type
          : field.fieldType && field.fieldType !== "integration"
            ? field.fieldType
            : null;
      if (integrationApiType)
        references.integrationApiTypes.add(integrationApiType);

      // Source mapping references
      if (field.sourceMapping) {
        if (field.sourceMapping.startsWith("business_unit_")) {
          const buCode = field.sourceMapping.replace("business_unit_", "");
          references.businessUnitCodes.add(buCode);
        }
        if (field.sourceMapping.startsWith("region_")) {
          const regionCode = field.sourceMapping.replace("region_", "");
          references.regionCodes.add(regionCode);
        }
      }

      // Options references
      if (
        field.extra?.api_url &&
        field.extra?.api_url.startsWith("extra-json/")
      ) {
        const extraJSONTag = field.extra.api_url.split("/")[1];
        references.extraJSONTags.add(extraJSONTag);
      }

      // Status indicators references
      if (field.statusIndicators) {
        Object.values(field.statusIndicators).forEach((indicator: any) => {
          if (indicator.tag) references.processStatusTags.add(indicator.tag);
        });
      }
    });

    // Rule level references
    Object.values(process.rules || {}).forEach(rule => {
      if (rule.actions) {
        Object.values(rule.actions).forEach((action: any) => {
          if (action.name === "send_notification" && action.params?.template) {
            references.emailTemplateSlug.add(action.params.template);
          }
          if (
            (action.name === "adjudicate_workflow" ||
              action.name === "adjudicate_integration_result") &&
            action.params?.flag
          ) {
            references.commentFlagOptionTags.add(action.params.flag);
          }
        });
      }
    });

    // Swimlane level references
    Object.values(process.swimlanes || {}).forEach(swimlane => {
      if (swimlane.staticGroups)
        swimlane.staticGroups.forEach(name => references.groupNames.add(name));
      if (swimlane.dynamicGroupNames)
        swimlane.dynamicGroupNames.forEach(name =>
          references.groupNames.add(name)
        );
    });
  });

  return references;
};

/**
 * Efficiently categorizes app settings using collected references and lookup maps
 */
const categorizeAppSettingsOptimized = (
  references: CollectedReferences,
  appSettingsLookupMaps: AppSettingsLookupMaps,
  categorized: AppSettingsArrays
): void => {
  // Email templates
  references.emailTemplateSlug.forEach(slug => {
    const emailTemplate = appSettingsLookupMaps.emailTemplateBySlug.get(slug);
    if (emailTemplate) categorized.emailTemplate.push(emailTemplate);
  });

  references.processKindTags.forEach(tag => {
    const processKind = appSettingsLookupMaps.processKindByTag.get(tag);
    if (processKind) categorized.processKind.push(processKind);
  });

  // Process statuses
  references.processStatusTags.forEach(tag => {
    const processStatus = appSettingsLookupMaps.processStatusByTag.get(tag);
    if (processStatus) categorized.processStatus.push(processStatus);
  });

  // User groups
  references.groupNames.forEach(name => {
    const group = appSettingsLookupMaps.groupByName.get(name);
    if (group) categorized.group.push(group);
  });

  // Business units
  references.businessUnitCodes.forEach(code => {
    const businessUnit = appSettingsLookupMaps.businessUnitByCode.get(code);
    if (businessUnit) categorized.businessUnit.push(businessUnit);
  });

  // Regions
  references.regionCodes.forEach(code => {
    const region = appSettingsLookupMaps.regionByCode.get(code);
    if (region) categorized.region.push(region);
  });

  // Extra JSON
  references.extraJSONTags.forEach(tag => {
    const extraJSON = appSettingsLookupMaps.extraJSONByTag.get(tag);
    if (extraJSON) categorized.extraJSON.push(extraJSON);
  });

  // Process PDF templates
  references.processPDFTemplateTags.forEach(tag => {
    const pdfTemplate = appSettingsLookupMaps.processPDFTemplateByTag.get(tag);
    if (pdfTemplate) categorized.processPDFTemplate.push(pdfTemplate);
  });

  // Comment flag options
  references.commentFlagOptionTags.forEach(tag => {
    const commentFlag = appSettingsLookupMaps.commentFlagOptionByTag.get(tag);
    if (commentFlag) categorized.commentFlagOption.push(commentFlag);
  });

  // Object type workflow kind relations
  references.workflowKindTags.forEach(workflowKind => {
    const relations =
      appSettingsLookupMaps.objectTypeWorkflowKindRelationByWorkflowKind.get(
        workflowKind
      );
    if (relations)
      categorized.objectTypeWorkflowKindRelation.push(...relations);
  });

  // Integration configs and tokens
  references.integrationApiTypes.forEach(apiType => {
    const integrationConfig =
      appSettingsLookupMaps.integrationConfigByApiType.get(apiType);

    if (integrationConfig) {
      categorized.integrationConfig.push(integrationConfig);

      // Also add the associated integration token if it exists
      if (integrationConfig.integrationToken) {
        const integrationToken =
          appSettingsLookupMaps.integrationTokenByTokenType.get(
            integrationConfig.integrationToken
          );
        if (integrationToken) {
          categorized.integrationToken.push(integrationToken);
        }
      }
    }
  });
};

/**
 * Main function to extract and analyze app settings from metaJSON(s)
 * Can handle both single Process object or array of Process objects
 * @param processes - Array of Process objects
 * @param appSettings - The actual app settings data to match against
 * @returns Object containing unique app settings used in the processes
 *
 * Performance: Uses optimized O(n + m) approach with lookup maps instead of O(n * m)
 * repeated find/filter operations for better performance with large datasets.
 */
export const analyzeAppSettings = (
  processes: Process[],
  appSettings: AppSettings = {
    ...emptyReferencedAppSettings,
    integrationConfig: [],
    integrationToken: []
  }
): {
  referencedAppSettings: ReferencedAppSettings;
} => {
  const categorized: AppSettingsArrays = {
    emailTemplate: [],
    emailAttachment: [],
    processKind: [],
    processStatus: [],
    objectStatus: [],
    processPDFTemplate: [],
    alertCategory: [],
    commentFlagOption: [],
    group: [],
    objectTypeWorkflowKindRelation: [],
    businessUnit: [],
    region: [],
    extraJSON: [],
    integrationConfig: [],
    integrationToken: []
  };

  // Optimized approach: collect all references first, then perform efficient lookups
  const references = collectReferencesFromProcesses(processes);
  const appSettingsLookupMaps = createAppSettingsLookupMaps(appSettings);

  // Efficiently categorize using maps for O(1) lookups
  categorizeAppSettingsOptimized(
    references,
    appSettingsLookupMaps,
    categorized
  );

  const uniqueAppSettings = getUniqueAppSettings(categorized);

  return {
    referencedAppSettings: uniqueAppSettings
  };
};

function getUniqueAppSettings(
  categorized: AppSettingsArrays
): ReferencedAppSettings {
  const unique: ReferencedAppSettings = {
    emailTemplate: [],
    emailAttachment: [],
    processKind: [],
    processStatus: [],
    objectStatus: [],
    processPDFTemplate: [],
    alertCategory: [],
    commentFlagOption: [],
    group: [],
    objectTypeWorkflowKindRelation: [],
    businessUnit: [],
    region: [],
    extraJSON: [],
    integrationConfig: [],
    integrationToken: []
  };

  Object.entries(categorized).forEach(([key, items]) => {
    const seen = new Set<string>();
    const filteredItems = items.filter((item: any) => {
      let identifier: string;

      // Special case for objectTypeWorkflowKindRelation - use composite key
      if (key === "objectTypeWorkflowKindRelation") {
        identifier = `${item.objectType}-${item.workflowKind}-${item.relation}`;
      } else if ("tag" in item && typeof item.tag === "string") {
        identifier = item.tag;
      } else if ("slug" in item && typeof item.slug === "string") {
        identifier = item.slug;
      } else if ("apiType" in item && typeof item.apiType === "string") {
        identifier = item.apiType;
      } else if ("tokenType" in item && typeof item.tokenType === "string") {
        identifier = item.tokenType;
      } else if ("name" in item && typeof item.name === "string") {
        identifier = item.name;
      } else if ("code" in item && typeof item.code === "string") {
        identifier = item.code;
      } else {
        identifier = String(item);
      }

      if (seen.has(identifier)) {
        return false;
      }
      seen.add(identifier);
      return true;
    });

    (unique as any)[key] = filteredItems;
  });

  return unique;
}

// Legacy extraction functions have been replaced with optimized approach above

export const joinReferencedAppSettings = (
  baseAppSettings: ReferencedAppSettings,
  referencedAppSettings: ReferencedAppSettings
): ReferencedAppSettings => {
  return {
    // Email templates: unique by 'slug'
    emailTemplate: mergeArraysByUniqueId(
      baseAppSettings.emailTemplate,
      referencedAppSettings.emailTemplate,
      "slug"
    ),

    // Email attachments: unique by 'title'
    emailAttachment: mergeArraysByUniqueId(
      baseAppSettings.emailAttachment,
      referencedAppSettings.emailAttachment,
      "title"
    ),

    // Business units: unique by 'code'
    businessUnit: mergeArraysByUniqueId(
      baseAppSettings.businessUnit,
      referencedAppSettings.businessUnit,
      "code"
    ),

    // Regions: unique by 'code'
    region: mergeArraysByUniqueId(
      baseAppSettings.region,
      referencedAppSettings.region,
      "code"
    ),

    // Extra JSON: unique by 'tag'
    extraJSON: mergeArraysByUniqueId(
      baseAppSettings.extraJSON,
      referencedAppSettings.extraJSON,
      "tag"
    ),

    // Process kinds: unique by 'tag'
    processKind: mergeArraysByUniqueId(
      baseAppSettings.processKind,
      referencedAppSettings.processKind,
      "tag"
    ),

    // Process statuses: unique by 'tag'
    processStatus: mergeArraysByUniqueId(
      baseAppSettings.processStatus,
      referencedAppSettings.processStatus,
      "tag"
    ),

    // Object statuses: unique by 'tag'
    objectStatus: mergeArraysByUniqueId(
      baseAppSettings.objectStatus,
      referencedAppSettings.objectStatus,
      "tag"
    ),

    // Process PDF templates: unique by 'tag'
    processPDFTemplate: mergeArraysByUniqueId(
      baseAppSettings.processPDFTemplate,
      referencedAppSettings.processPDFTemplate,
      "tag"
    ),

    // Alert categories: unique by 'tag'
    alertCategory: mergeArraysByUniqueId(
      baseAppSettings.alertCategory,
      referencedAppSettings.alertCategory,
      "tag"
    ),

    // Comment flag options: unique by 'tag'
    commentFlagOption: mergeArraysByUniqueId(
      baseAppSettings.commentFlagOption,
      referencedAppSettings.commentFlagOption,
      "tag"
    ),

    // User groups: unique by 'name'
    group: mergeArraysByUniqueId(
      baseAppSettings.group,
      referencedAppSettings.group,
      "name"
    ),

    // Object type workflow kind relations: unique by composite key (objectType-workflowKind-relation)
    objectTypeWorkflowKindRelation: (() => {
      const referencedMap = new Map(
        referencedAppSettings.objectTypeWorkflowKindRelation.map(item => [
          `${item.objectType}-${item.workflowKind}-${item.relation}`,
          item
        ])
      );

      return baseAppSettings.objectTypeWorkflowKindRelation
        .map(item => {
          const key = `${item.objectType}-${item.workflowKind}-${item.relation}`;
          return referencedMap.has(key) ? referencedMap.get(key)! : item;
        })
        .concat(
          referencedAppSettings.objectTypeWorkflowKindRelation.filter(item => {
            const key = `${item.objectType}-${item.workflowKind}-${item.relation}`;
            return !baseAppSettings.objectTypeWorkflowKindRelation.some(
              baseItem =>
                `${baseItem.objectType}-${baseItem.workflowKind}-${baseItem.relation}` ===
                key
            );
          })
        );
    })(),

    // Integration configs: unique by 'apiType'
    integrationConfig: mergeArraysByUniqueId(
      baseAppSettings.integrationConfig || [],
      referencedAppSettings.integrationConfig || [],
      "apiType"
    ),

    // Integration tokens: unique by 'tokenType'
    integrationToken: mergeArraysByUniqueId(
      baseAppSettings.integrationToken || [],
      referencedAppSettings.integrationToken || [],
      "tokenType"
    )
  };
};
